<template>
  <div v-loading="loading">
    <el-empty
      description="暂无项目"
      v-if="!projects || projects.length === 0"
    ></el-empty>

    <el-table
      v-else
      :data="projects"
      stripe
      style="width: 100%"
      border
      size="large"
      :header-cell-style="{
        backgroundColor: 'rgba(245, 247, 250, 0.7)',
        color: '#303133',
      }"
      height="calc(100vh - 440px)"
    >
      <el-table-column
        prop="name"
        label="项目名称"
        min-width="400"
        show-overflow-tooltip
      >
        <template #default="{ row }">
          <div class="flex items-center">
            <el-tag
              class="cursor-pointer"
              size="large"
              effect="dark"
              @click="$emit('view', row)"
              ><el-text size="large" style="color: #ffffff">{{
                row.name
              }}</el-text></el-tag
            >
            <el-tag class="mx-[12px]" plain size="large"
              >容器：{{ row.containerName }}</el-tag
            >
            <el-tag v-if="type === 'build'" plain size="large"
              >分支：{{ row.detail?.branch }}</el-tag
            >
          </div>
        </template>
      </el-table-column>
      <el-table-column
        v-if="type === 'build'"
        prop="commit"
        label="commit"
        min-width="120"
        show-overflow-tooltip
      >
        <template #default="{ row }">
          <!-- 取前七位 -->
          <el-tag v-if="row.commit" plain>{{
            row.commit?.substring(0, 7)
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="端口映射" min-width="140" show-overflow-tooltip>
        <template #default="scope">
          <div
            v-for="(port, idx) in scope.row.portMaps"
            :key="idx"
            class="text-xs"
          >
            <el-tag type="info" class="mb-[4px]"
              >{{ port.hostPort }} / {{ port.containerPort }}</el-tag
            >
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="status"
        label="状态"
        min-width="140"
        show-overflow-tooltip
      >
        <template #default="scope">
          <div class="flex items-center justify-between">
            <!-- <div
                v-if="
                  (scope.row.status === 'deployFailure' ||
                    scope.row.status === 'destroyFailure') &&
                  scope.row.errMsg
                "
                class="text-wrap"
              >
                <el-tooltip placement="top" class="inline-block" effect="light">
                  <el-tag :type="getStatus(scope.row.status).color">
                    {{ getStatus(scope.row.status).label }}
                  </el-tag>
                  <template #content>
                    <div class="w-[300px] text-wrap">
                      {{ scope.row.errMsg }}
                    </div>
                  </template>
                </el-tooltip>

              </div> -->
            <el-tag
              class="cursor-pointer"
              :type="getStatus(scope.row.status).color"
              @click="handleStatusClick(scope.row)"
            >
              {{ getStatus(scope.row.status).label }}
            </el-tag>
            <span
              v-if="scope.row.runStatus"
              class="status-dot"
              :class="scope.row.runStatus"
            ></span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="部署" min-width="100">
        <template #default="{ row }">
          <el-popconfirm
            title="确定要部署这个项目吗？"
            confirm-button-text="确定"
            cancel-button-text="取消"
            @confirm="$emit('deploy', row.id, row.envID)"
            width="200"
          >
            <template #reference>
              <el-button
                type="primary"
                size="small"
                :icon="Upload"
                :disabled="hasDisabled(row.status)"
                >部署
              </el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
      <el-table-column label="更新时间" min-width="170" show-overflow-tooltip>
        <template #default="{ row }">
          <el-tag v-if="row.updateAt" plain>
            {{ new Date(row.updateAt * 1000).toLocaleString("zh-CN") }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        label="操作"
        width="140"
        fixed="right"
        show-overflow-tooltip
        class-name="operation-column"
      >
        <template #default="scope">
          <el-button-group class="mb-4">
            <el-button
              size="small"
              type="primary"
              @click="$emit('edit', scope.row)"
              :icon="Edit"
              title="编辑"
            >
            </el-button>
            <el-button
              size="small"
              @click="$emit('view', scope.row)"
              :icon="View"
              title="详情"
            >
            </el-button>
            <el-popconfirm
              title="确定要销毁这个项目吗？"
              confirm-button-text="确定"
              cancel-button-text="取消"
              @confirm="$emit('destroy', scope.row.id, scope.row.envID)"
              width="200"
            >
              <template #reference>
                <el-button
                  type="danger"
                  size="small"
                  :icon="Delete"
                  :disabled="hasDisabled(scope.row.status)"
                  title="销毁"
                >
                </el-button>
              </template>
            </el-popconfirm>
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>

    <el-drawer title="错误信息" v-model="showDrawer" size="80%" direction="ltr">
      <div class="text-[#000000]">
        {{ errMsg }}
      </div>
    </el-drawer>
  </div>
</template>

<script setup lang="ts">
import { Upload, View, Delete, Edit } from "@element-plus/icons-vue";
import type { Project } from "../types";
import { getStatus } from "./utils";
import { ref } from "vue";

defineProps<{
  type: "build" | "image";
  projects: Project[] | undefined;
  loading: boolean;
}>();

defineEmits<{
  (e: "deploy", id: number, envId: number): void;
  (e: "view", project: Project): void;
  (e: "destroy", id: number, envId: number): void;
  (e: "edit", project: Project): void;
}>();

function hasDisabled(status: string) {
  return status === "deploying" || status === "destroying";
}

const errMsg = ref("")
const showDrawer = ref(false)
function handleStatusClick(row: Project) {
  if (
    (row.status === "deployFailure" || row.status === "destroyFailure") &&
    row.errMsg
  ) {
    errMsg.value =  row.errMsg
    showDrawer.value = true;
  }
}
</script>

<style scoped>
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 4px;
}

.status-dot.alive {
  background-color: #67c23a;
  box-shadow: 0 0 8px #67c23a;
  animation: blink 1.5s infinite;
}

.status-dot.death {
  background-color: #f56c6c;
  box-shadow: 0 0 8px #f56c6c;
  animation: blink-death 1.5s infinite;
}

/* alive的动画 */
@keyframes blink {
  0% {
    opacity: 1;
    box-shadow: 0 0 8px #67c23a;
  }
  50% {
    opacity: 0.4;
    box-shadow: 0 0 2px #67c23a;
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 8px #67c23a;
  }
}

/* death的动画 */
@keyframes blink-death {
  0% {
    opacity: 1;
    box-shadow: 0 0 8px #f56c6c;
  }
  50% {
    opacity: 0.4;
    box-shadow: 0 0 2px #f56c6c;
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 8px #f56c6c;
  }
}
</style>
